import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { API_BASE_URL, getApiUrl } from "../utils/api";
import { io } from "socket.io-client";
import { UserContext } from "./UserContext";

const ChatContext = createContext();

const getMessagePartyId = (party) => {
  if (!party) return "";
  if (typeof party === "string") return party;
  return String(party._id || party.id || "");
};

const appendUniqueMessage = (prev, message) => {
  if (!message?._id) return [...prev, message];
  return prev.some((m) => String(m._id) === String(message._id))
    ? prev
    : [...prev, message];
};

const isMessageForConversation = (conversation, message, conversationId) => {
  if (!conversation) return false;
  if (String(conversation.conversationId) === String(conversationId)) return true;
  if (!String(conversation.conversationId || "").startsWith("temp-")) return false;
  const otherUserId = getMessagePartyId(conversation.otherUser);
  if (!otherUserId) return false;
  const senderId = getMessagePartyId(message.sender);
  const receiverId = getMessagePartyId(message.receiver);
  return senderId === otherUserId || receiverId === otherUserId;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user } = useContext(UserContext);
  const [socket, setSocket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationsPagination, setConversationsPagination] = useState({
    currentPage: 1,
    hasNextPage: false,
    totalItems: 0,
  });
  const [loadingMoreConversations, setLoadingMoreConversations] =
    useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadReady, setUnreadReady] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [notifications, setNotifications] = useState([]);
  const [notificationsPagination, setNotificationsPagination] = useState({
    currentPage: 1,
    hasNextPage: false,
    totalItems: 0,
  });
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [loadingMoreNotifications, setLoadingMoreNotifications] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesSkip, setMessagesSkip] = useState(0);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [poemUpdates, setPoemUpdates] = useState({});
  const [conversationSeenAtMap, setConversationSeenAtMap] = useState({});
  const [conversationSeenMessageIdMap, setConversationSeenMessageIdMap] = useState({});
  const [conversationDeliveredMessageIdMap, setConversationDeliveredMessageIdMap] = useState({});
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [chatModalPreSelectedUser, setChatModalPreSelectedUser] =
    useState(null);
  const typingTimeoutRef = useRef({});
  const typingLastAtRef = useRef({});
  const socketRef = useRef(null);
  const currentConversationRef = useRef(currentConversation);
  const messagesRef = useRef(messages);
  const lastUnreadUpdateRef = useRef(0);
  const lightRefreshTimerRef = useRef(null);
  const markReadWhileViewingTimerRef = useRef(null);

  // Placeholder; initialized later after functions are defined
  let requestLightRefresh = () => {};

  // Update the ref whenever currentConversation changes
  useEffect(() => {
    currentConversationRef.current = currentConversation;
  }, [currentConversation]);

  // Helper function to compute and store the seen message ID
  const updateSeenMessageId = useCallback((conversationId, seenAt) => {
    try {
      const currentUser = user || JSON.parse(localStorage.getItem("user"));
      if (!currentUser || !seenAt) return;

      const currentConv = currentConversationRef.current;
      // Only compute if this is the current conversation (messages are loaded)
      if (!currentConv || String(currentConv.conversationId) !== String(conversationId)) {
        // If not current conversation, store just the seenAt time for later
        return;
      }

      const msgs = messagesRef.current || [];
      if (msgs.length === 0) return;

      const ourMessages = msgs.filter(
        (m) => m && m.sender && String(m.sender._id) === String(currentUser._id)
      );

      if (ourMessages.length === 0) return;

      const seenTime = new Date(seenAt).getTime();
      if (isNaN(seenTime)) return;

      // Find the last (latest) of our messages created at or before seenAt
      // Messages are in chronological order (oldest to newest)
      let lastSeenId = null;
      for (let i = ourMessages.length - 1; i >= 0; i--) {
        const t = new Date(ourMessages[i].createdAt).getTime();
        if (!isNaN(t) && t <= seenTime) {
          lastSeenId = ourMessages[i]._id;
          break;
        }
      }

      if (lastSeenId) {
        setConversationSeenMessageIdMap((prev) => ({
          ...prev,
          [conversationId]: String(lastSeenId),
        }));
      }
    } catch (error) {
      console.error("Error updating seen message ID:", error);
    }
  }, [user]);

  // Update the ref whenever messages change
  useEffect(() => {
    messagesRef.current = messages;
    
    // Recompute seen message ID when messages change for the current conversation
    if (currentConversation?.conversationId) {
      const seenAt = conversationSeenAtMap[currentConversation.conversationId];
      if (seenAt) {
        updateSeenMessageId(currentConversation.conversationId, seenAt);
      }
    }
  }, [messages, currentConversation, conversationSeenAtMap, updateSeenMessageId]);

  // Request notification permission on component mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Initialize socket connection
  useEffect(() => {
    // Only connect socket if user is authenticated
    if (!user?._id) {
      setUnreadReady(false);
      // Disconnect existing socket if user logged out
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    // Authentication handled via cookies (preferred method)
    // Socket.io will automatically send cookies with the connection

    // Prevent multiple socket connections
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Try to get token from localStorage as fallback, but prefer cookies
    // Socket.io will automatically send cookies if CORS is configured correctly
    const tokenFromStorage =
      (typeof window !== "undefined" && localStorage.getItem("token")) || null;
    const newSocket = io(API_BASE_URL, {
      auth: tokenFromStorage ? { token: tokenFromStorage } : undefined,
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 3,
      reconnectionDelay: 500,
      timeout: 5000,
      forceNew: true,
    });

    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      setIsConnected(true);
    });

    newSocket.on("disconnect", (reason) => {
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      // Only log authentication errors, not connection refused errors
      if (error.message && error.message.includes("Authentication")) {
        console.error("Socket connection error:", error);
      }
      setIsConnected(false);
    });

    newSocket.on("reconnect", (attemptNumber) => {
      setIsConnected(true);
    });

    newSocket.on("reconnect_error", (error) => {
      console.error("Socket reconnection error:", error);
    });

    newSocket.on("newMessage", ({ message, conversationId }) => {
      // Add new message to current conversation if it's the active one
      const currentConv = currentConversationRef.current;

      const isViewingThisConversation = isMessageForConversation(
        currentConv,
        message,
        conversationId
      );

      if (
        isViewingThisConversation &&
        currentConv?.conversationId?.startsWith("temp-")
      ) {
        setCurrentConversation({
          ...currentConv,
          conversationId,
        });
        if (socketRef.current) {
          socketRef.current.emit("joinConversation", conversationId);
        }
      }

      if (isViewingThisConversation) {
        setMessages((prev) => appendUniqueMessage(prev, message));
      }

      // Check if the current user is the sender
      const currentUser = user || JSON.parse(localStorage.getItem("user"));
      // Compare as strings to ensure proper comparison
      const isCurrentUserSender =
        currentUser && String(message.sender._id) === String(currentUser._id);

      // Update conversations list with new message (optimized)
      setConversations((prev) => {
        const conversationIndex = prev.findIndex(
          (conv) => String(conv.conversationId) === String(conversationId)
        );

        if (conversationIndex !== -1) {
          // Update only the specific conversation
          const updated = [...prev];
          const currentUnreadCount =
            updated[conversationIndex].unreadCount || 0;

          updated[conversationIndex] = {
            ...updated[conversationIndex],
            lastMessage: message,
            // Only bump unread when we're not the sender and we're not already viewing this thread
            unreadCount: isCurrentUserSender
              ? currentUnreadCount
              : isViewingThisConversation
                ? currentUnreadCount
                : currentUnreadCount + 1,
          };

          return updated;
        } else {
          // New conversation - return current state, let periodic refresh handle it
          return prev;
        }
      });

      // Mark delivered for our outgoing message (so UI shows Delivered until Seen)
      if (isCurrentUserSender) {
        setConversationDeliveredMessageIdMap((prev) => ({
          ...prev,
          [conversationId]: String(message._id),
        }));
      }

      // Update unread count only if user is not viewing this conversation AND is not the sender
      const shouldUpdateUnreadCount =
        !isCurrentUserSender &&
        (!currentConv ||
          String(currentConv.conversationId) !== String(conversationId));

      if (shouldUpdateUnreadCount) {
        setUnreadCount((prev) => {
          const newCount = prev + 1;
          lastUnreadUpdateRef.current = Date.now(); // Record when we updated locally
          return newCount;
        });
      }

      // While viewing this thread, the server still increments unread on send — clear it so the badge doesn't stick at 1
      if (!isCurrentUserSender && isViewingThisConversation) {
        if (markReadWhileViewingTimerRef.current) {
          clearTimeout(markReadWhileViewingTimerRef.current);
        }
        markReadWhileViewingTimerRef.current = setTimeout(() => {
          markReadWhileViewingTimerRef.current = null;
          const token =
            (typeof window !== "undefined" &&
              localStorage.getItem("token")) ||
            null;
          fetch(
            getApiUrl(`/api/chat/mark-read/${encodeURIComponent(
              String(conversationId)
            )}`),
            {
              method: "PUT",
              credentials: "include",
              headers: token
                ? { Authorization: `Bearer ${token}` }
                : undefined,
            }
          )
            .then((res) => {
              if (!res.ok) return;
              const t =
                (typeof window !== "undefined" &&
                  localStorage.getItem("token")) ||
                null;
              return fetch(getApiUrl("/api/chat/conversations"), {
                credentials: "include",
                headers: t ? { Authorization: `Bearer ${t}` } : undefined,
              }).then((r) => (r.ok ? r.json() : null));
            })
            .then((data) => {
              if (Array.isArray(data)) {
                const totalUnread = data.reduce(
                  (sum, c) =>
                    sum +
                    (typeof c.unreadCount === "number" ? c.unreadCount : 0),
                  0
                );
                setConversations(data);
                setUnreadCount(totalUnread);
                lastUnreadUpdateRef.current = Date.now();
              }
            })
            .catch(() => {});
        }, 400);
      }

      // Show browser notification if user is not viewing the conversation AND is not the sender
      // Also consider if the chat modal is open and focused on a different conversation
      const shouldShowNotification =
        !isCurrentUserSender && !isViewingThisConversation;

      if (shouldShowNotification) {
        if ("Notification" in window) {
          if (Notification.permission === "granted") {
            new Notification("New Message", {
              body: `${message.sender.username}: ${message.content}`,
              icon: "/favicon.ico",
            });
          } else if (Notification.permission === "default") {
            Notification.requestPermission().then((permission) => {
              if (permission === "granted") {
                new Notification("New Message", {
                  body: `${message.sender.username}: ${message.content}`,
                  icon: "/favicon.ico",
                });
              }
            });
          }
        }
      }
    });

    newSocket.on("userTyping", ({ userId, username }) => {
      const id = String(userId);
      // Add to set
      setTypingUsers((prev) => new Set([...prev, id]));
      // Record last typing timestamp
      typingLastAtRef.current[id] = Date.now();
      // Debounced auto-clear if no follow-up typing
      if (typingTimeoutRef.current[id]) {
        clearTimeout(typingTimeoutRef.current[id]);
      }
      typingTimeoutRef.current[id] = setTimeout(() => {
        setTypingUsers((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        delete typingLastAtRef.current[id];
        delete typingTimeoutRef.current[id];
      }, 1000);
    });

    newSocket.on("userStopTyping", ({ userId }) => {
      const id = String(userId);
      // Remove from live set immediately; keep lastAt and existing timer from userTyping
      setTypingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      // Do not clear typingLastAtRef or its timeout here; let the previous 2s timer expire
    });

    newSocket.on("group_new_post", (payload) => {
      if (payload?.groupId) {
        window.dispatchEvent(
          new CustomEvent("group-new-post", { detail: payload })
        );
      }
    });

    newSocket.on("group_membership_updated", (payload) => {
      if (!payload?.groupId) return;
      window.dispatchEvent(
        new CustomEvent("groups-updated", { detail: { groupId: payload.groupId } })
      );
      window.dispatchEvent(
        new CustomEvent("group-membership-changed", { detail: payload })
      );
    });

    // Handle notifications
    newSocket.on("notification", (notif) => {
      if (
        notif?.type === "group_join_request_response" &&
        notif?.meta?.groupId
      ) {
        const groupId = String(
          notif.meta.groupId?._id || notif.meta.groupId
        );
        window.dispatchEvent(
          new CustomEvent("groups-updated", { detail: { groupId } })
        );
        window.dispatchEvent(
          new CustomEvent("group-membership-changed", {
            detail: {
              groupId,
              joinRequestStatus: notif.meta.requestStatus,
              isMember: notif.meta.requestStatus === "accepted",
            },
          })
        );
      }

      // If notification doesn't have _id, fetch full list to get it
      // Otherwise add it to the list
      if (!notif._id && !notif.id) {
        // Fetch notifications to get the _id for the new one
        fetchNotifications();
      } else {
        setNotifications((prev) => {
          const idStr = String(notif._id || notif.id);
          if (prev.some((n) => String(n._id || n.id) === idStr)) return prev;
          return [notif, ...prev];
        });
        if (!notif.read) {
          setNotificationUnreadCount((prev) => prev + 1);
        }
      }
    });

    // Read receipts
    newSocket.on("messagesSeen", ({ conversationId, readerId, seenAt }) => {
      // Store last seen time for the conversation from the other participant
      setConversationSeenAtMap((prev) => ({
        ...prev,
        [conversationId]: seenAt,
      }));

      // Update seen message ID using the helper (with a small delay to ensure messages ref is updated)
      setTimeout(() => {
        updateSeenMessageId(conversationId, seenAt);
      }, 100);
    });

    // Handle poem updates
    newSocket.on("poemUpdated", ({ poemId, updatedPoem }) => {
      setPoemUpdates((prev) => ({
        ...prev,
        [poemId]: updatedPoem,
      }));
    });

    setSocket(newSocket);

    return () => {
      if (markReadWhileViewingTimerRef.current) {
        clearTimeout(markReadWhileViewingTimerRef.current);
        markReadWhileViewingTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user?._id]); // Recreate socket when user changes

  // Get unread count
  const fetchUnreadCount = useCallback(async (force = false) => {
    // Only fetch if user is authenticated
    if (!user?._id) {
      setUnreadCount(0);
      return;
    }

    try {
      // Don't overwrite if we recently updated the count locally (within last 10 seconds)
      // unless force is true
      const timeSinceLastUpdate = Date.now() - lastUnreadUpdateRef.current;
      if (!force && timeSinceLastUpdate < 10000) {
        return;
      }

      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      const response = await fetch(
        getApiUrl("/api/chat/unread-count"),
        {
          credentials: "include", // Use cookies for authentication
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unreadCount);
        lastUnreadUpdateRef.current = Date.now();
        setUnreadReady(true);
      } else if (response.status === 401) {
        // User is not authenticated, set count to 0
        setUnreadCount(0);
        setUnreadReady(true);
      }
      // Silently ignore other errors
    } catch (error) {
      // Only log non-network errors
      if (error.name !== "TypeError") {
        console.error("Error fetching unread count:", error);
      }
    }
  }, [user?._id]);

  // Fetch notifications (paginated)
  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    // Only fetch if user is authenticated
    if (!user?._id) {
      setNotifications([]);
      setNotificationUnreadCount(0);
      setNotificationsPagination({
        currentPage: 1,
        hasNextPage: false,
        totalItems: 0,
      });
      return;
    }

    try {
      if (append) setLoadingMoreNotifications(true);
      const response = await fetch(
        getApiUrl(`/api/user/notifications?page=${page}&limit=20`),
        {
          credentials: "include", // Use cookies for authentication
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Support legacy array response and new paginated shape
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.notifications)
            ? data.notifications
            : [];
        const pagination = Array.isArray(data)
          ? {
              currentPage: 1,
              hasNextPage: false,
              totalItems: list.length,
            }
          : {
              currentPage: data.pagination?.currentPage ?? page,
              hasNextPage: Boolean(data.pagination?.hasNextPage),
              totalItems: data.pagination?.totalItems ?? list.length,
              totalPages: data.pagination?.totalPages ?? 1,
            };

        setNotifications((prev) => (append ? [...prev, ...list] : list));
        setNotificationsPagination(pagination);
        if (typeof data.unreadCount === "number") {
          setNotificationUnreadCount(data.unreadCount);
        } else {
          setNotificationUnreadCount(list.filter((n) => !n.read).length);
        }
      } else if (response.status === 401) {
        // User is not authenticated, clear notifications
        setNotifications([]);
        setNotificationUnreadCount(0);
      }
      // Silently ignore other errors
    } catch (error) {
      // Only log non-network errors
      if (error.name !== "TypeError") {
        console.error("Error fetching notifications:", error);
      }
    } finally {
      if (append) setLoadingMoreNotifications(false);
    }
  }, [user?._id]);

  const loadMoreNotifications = useCallback(async () => {
    if (
      loadingMoreNotifications ||
      !notificationsPagination.hasNextPage
    ) {
      return;
    }
    await fetchNotifications(
      (notificationsPagination.currentPage || 1) + 1,
      true
    );
  }, [
    loadingMoreNotifications,
    notificationsPagination.hasNextPage,
    notificationsPagination.currentPage,
    fetchNotifications,
  ]);

  // Fetch conversations (paginated)
  const fetchConversations = useCallback(async (page = 1, append = false) => {
    try {
      if (append) setLoadingMoreConversations(true);
      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      const response = await fetch(
        getApiUrl(`/api/chat/conversations?page=${page}&limit=30`),
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.conversations)
            ? data.conversations
            : [];
        const pagination = Array.isArray(data)
          ? {
              currentPage: 1,
              hasNextPage: false,
              totalItems: list.length,
            }
          : {
              currentPage: data.pagination?.currentPage ?? page,
              hasNextPage: Boolean(data.pagination?.hasNextPage),
              totalItems: data.pagination?.totalItems ?? list.length,
              totalPages: data.pagination?.totalPages ?? 1,
            };

        setConversations((prev) => {
          if (!append) return list;
          const existing = new Set(prev.map((c) => String(c.conversationId)));
          return [
            ...prev,
            ...list.filter(
              (c) => c?.conversationId && !existing.has(String(c.conversationId))
            ),
          ];
        });
        setConversationsPagination(pagination);

        const totalUnread =
          typeof data.unreadCount === "number"
            ? data.unreadCount
            : list.reduce(
                (sum, c) =>
                  sum + (typeof c.unreadCount === "number" ? c.unreadCount : 0),
                0
              );
        setUnreadCount(totalUnread);
        lastUnreadUpdateRef.current = Date.now();
        setUnreadReady(true);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      if (append) setLoadingMoreConversations(false);
    }
  }, []);

  const loadMoreConversations = useCallback(async () => {
    if (
      loadingMoreConversations ||
      !conversationsPagination.hasNextPage
    ) {
      return;
    }
    await fetchConversations(
      (conversationsPagination.currentPage || 1) + 1,
      true
    );
  }, [
    loadingMoreConversations,
    conversationsPagination.hasNextPage,
    conversationsPagination.currentPage,
    fetchConversations,
  ]);

  // Fetch notifications when socket connects
  useEffect(() => {
    if (isConnected) {
      fetchNotifications();
    }
  }, [isConnected, fetchNotifications]);

  // Fetch notifications and conversations when user is available
  useEffect(() => {
    if (user && user._id) {
      fetchNotifications();
      fetchConversations();
    }
  }, [user, fetchNotifications, fetchConversations]);

  // Periodically refresh conversations; unread badge is derived from visible conversations
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      const timeSinceLastUpdate = Date.now() - lastUnreadUpdateRef.current;
      if (timeSinceLastUpdate > 30000) {
        fetchConversations();
      }
    }, 60000); // Refresh every 60 seconds

    return () => clearInterval(interval);
  }, [isConnected, fetchConversations]);

  // Debounced lightweight refresh to avoid spamming server
  requestLightRefresh = useCallback(() => {
    if (lightRefreshTimerRef.current) {
      clearTimeout(lightRefreshTimerRef.current);
    }
    lightRefreshTimerRef.current = setTimeout(() => {
      fetchConversations();
      lightRefreshTimerRef.current = null;
    }, 300);
  }, [fetchConversations]);

  // Fetch messages for a conversation
  const fetchMessages = async (
    conversationId,
    limit = 50,
    skip = 0,
    { append = false } = {}
  ) => {
    try {
      if (append) setLoadingOlderMessages(true);
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
      });

      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      const response = await fetch(
        getApiUrl(`/api/chat/messages/${conversationId}?${queryParams}`),
        {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Handle new response format with pagination
        const nextMessages = data.messages
          ? data.messages
          : Array.isArray(data)
            ? data
            : [];
        const hasMore = Boolean(data.pagination?.hasMore);

        if (append) {
          setMessages((prev) => {
            const existing = new Set(prev.map((m) => String(m._id)));
            const older = nextMessages.filter(
              (m) => m?._id && !existing.has(String(m._id))
            );
            return [...older, ...prev];
          });
          setMessagesSkip(skip + nextMessages.length);
        } else {
          setMessages(nextMessages);
          setMessagesSkip(nextMessages.length);
        }
        setMessagesHasMore(hasMore);

        // Update seen message ID if we have a seenAt timestamp for this conversation
        const seenAtIso = conversationSeenAtMap[conversationId];
        if (seenAtIso) {
          // Use setTimeout to ensure state is updated before computing
          setTimeout(() => {
            updateSeenMessageId(conversationId, seenAtIso);
          }, 100);
        }

        // Join conversation room
        if (socket) {
          socket.emit("joinConversation", conversationId);
        }

        // Fetch last seen for this conversation to persist after refresh
        try {
          const token =
            (typeof window !== "undefined" && localStorage.getItem("token")) ||
            null;
          const lsRes = await fetch(
            getApiUrl(`/api/chat/last-seen/${conversationId}`),
            {
              credentials: "include",
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
          if (lsRes.ok) {
            const data = await lsRes.json();
            if (data && data.messageId) {
              setConversationSeenMessageIdMap((prev) => ({
                ...prev,
                [conversationId]: String(data.messageId),
              }));
              if (data.seenAt) {
                setConversationSeenAtMap((prev) => ({
                  ...prev,
                  [conversationId]: data.seenAt,
                }));
              }
            }
          }
        } catch (err) {
          // silent
        }
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      if (append) setLoadingOlderMessages(false);
    }
  };

  const loadOlderMessages = async (conversationId) => {
    if (!conversationId || loadingOlderMessages || !messagesHasMore) return;
    await fetchMessages(conversationId, 50, messagesSkip, { append: true });
  };

  // Create a conversation without sending a message
  const createConversation = async (receiverId) => {
    try {
      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      const response = await fetch(
        getApiUrl("/api/chat/create-conversation"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include", // Include cookies for authentication
          body: JSON.stringify({ receiverId }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.conversationId;
      } else {
        let errorMessage = "Unable to message this user";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (_) {
          const errorText = await response.text();
          try {
            const parsed = JSON.parse(errorText);
            errorMessage = parsed.error || errorMessage;
          } catch (_) {
            // Use default message if parsing fails
          }
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error creating conversation:", error);
      throw error;
    }
  };

  // Send a message
  const sendMessage = async (receiverId, content) => {
    try {
      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      const response = await fetch(getApiUrl("/api/chat/send"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({ receiverId, content }),
      });

      if (response.ok) {
        const data = await response.json();
        const sentMessage = data.message || data;
        const conversationId = sentMessage.conversationId;
        const currentConv = currentConversationRef.current;
        const isActiveThread = isMessageForConversation(
          currentConv,
          sentMessage,
          conversationId
        );

        if (isActiveThread) {
          if (currentConv?.conversationId?.startsWith("temp-")) {
            setCurrentConversation({
              ...currentConv,
              conversationId,
            });
            if (socketRef.current) {
              socketRef.current.emit("joinConversation", conversationId);
            }
          }
          setMessages((prev) => appendUniqueMessage(prev, sentMessage));
          setConversationDeliveredMessageIdMap((prev) => ({
            ...prev,
            [conversationId]: String(sentMessage._id),
          }));
        }

        setConversations((prev) => {
          const idx = prev.findIndex(
            (c) => String(c.conversationId) === String(conversationId)
          );
          if (idx === -1) {
            requestLightRefresh();
            return prev;
          }
          const updated = [...prev];
          updated[idx] = { ...updated[idx], lastMessage: sentMessage };
          return updated;
        });

        return sentMessage;
      } else {
        let errorMessage = "Unable to message this user";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (_) {
          const errorText = await response.text();
          try {
            const parsed = JSON.parse(errorText);
            errorMessage = parsed.error || errorMessage;
          } catch (_) {
            // Use default message if parsing fails
          }
        }
        console.error("Error sending message:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      throw error;
    }
  };

  // Set typing indicator
  const setTyping = (conversationId, isTyping) => {
    if (!socket || !conversationId) return;

    const user = JSON.parse(localStorage.getItem("user"));

    if (isTyping) {
      socket.emit("typing", {
        conversationId,
        userId: user._id,
        username: user.username,
      });
    } else {
      socket.emit("stopTyping", {
        conversationId,
        userId: user._id,
      });
    }
  };

  // Utility: is a given user actively typing within grace window?
  const isUserTypingActive = (userId, graceMs = 1500) => {
    const id = String(userId || "");
    const last = typingLastAtRef.current[id];
    if (!last) return false;
    return Date.now() - last <= graceMs;
  };

  // Mark conversation as read
  const markAsRead = async (conversationId) => {
    try {
      // Immediate local clear for snappy UI and correct badge behavior
      setConversations((prev) => {
        let clearedAmount = 0;
        const updated = prev.map((conv) => {
          const isMatch =
            conv.conversationId === conversationId ||
            String(conv._id) === String(conversationId);
          if (isMatch) {
            const count = typeof conv.unreadCount === "number" ? conv.unreadCount : 0;
            clearedAmount = count;
            return { ...conv, unreadCount: 0 };
          }
          return conv;
        });
        // Reduce global unread count immediately
        if (clearedAmount > 0) {
          setUnreadCount((prevTotal) => Math.max(0, prevTotal - clearedAmount));
        }
        return updated;
      });

      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      await fetch(getApiUrl(`/api/chat/mark-read/${conversationId}`),
        {
          method: "PUT",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      // Debounced refresh to avoid duplicate requests when user navigates quickly
      requestLightRefresh();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  // Mark notifications as read
  const markNotificationsAsRead = async () => {
    try {
      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      await fetch(getApiUrl("/api/user/notifications/read-all"), {
        method: "PUT",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotificationUnreadCount(0);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const resolveNotificationId = async (notificationId, sourceNotification) => {
    if (notificationId) return String(notificationId);
    if (!sourceNotification) return null;

    try {
      const response = await fetch(
        getApiUrl("/api/user/notifications"),
        { credentials: "include" }
      );
      if (!response.ok) return null;

      const data = await response.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.notifications)
          ? data.notifications
          : [];
      if (!list.length) return null;

      const sourceDate = new Date(sourceNotification.date).getTime();
      const match = list.find((n) => {
        if (n.read) return false;
        if (n.type !== sourceNotification.type) return false;
        if (n.message !== sourceNotification.message) return false;
        const dateDiff = Math.abs(new Date(n.date).getTime() - sourceDate);
        if (Number.isFinite(sourceDate) && dateDiff > 10000) return false;
        return !!(n._id || n.id);
      });

      return match ? String(match._id || match.id) : null;
    } catch {
      return null;
    }
  };

  const applyNotificationRead = (targetId, sourceNotification = null) => {
    const idStr = targetId ? String(targetId) : "";
    const sourceDate = sourceNotification?.date
      ? new Date(sourceNotification.date).getTime()
      : null;

    setNotifications((prev) =>
      prev.map((n) => {
        const nId = n._id || n.id;
        const nIdStr = nId ? String(nId) : "";

        const idMatches =
          idStr &&
          (nIdStr === idStr ||
            String(nId) === idStr ||
            (nId?.toString && nId.toString() === idStr));

        const sourceMatches =
          sourceNotification &&
          n.type === sourceNotification.type &&
          n.message === sourceNotification.message &&
          !n.read &&
          (!sourceDate ||
            Math.abs(new Date(n.date).getTime() - sourceDate) <= 10000);

        if (idMatches || sourceMatches) {
          return { ...n, read: true, _id: n._id || n.id || targetId };
        }
        return n;
      })
    );
    setNotificationUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const patchNotification = useCallback((notificationId, patch) => {
    const idStr = notificationId ? String(notificationId) : "";
    if (!idStr) return;

    setNotifications((prev) =>
      prev.map((n) => {
        const nId = String(n._id || n.id || "");
        if (nId !== idStr) return n;
        return {
          ...n,
          ...patch,
          meta: patch.meta ? { ...(n.meta || {}), ...patch.meta } : n.meta,
        };
      })
    );
  }, []);

  // Mark a single notification as read
  const markNotificationAsRead = async (
    notificationId,
    sourceNotification = null
  ) => {
    let idToMark = notificationId ? String(notificationId) : null;
    if (!idToMark) {
      idToMark = await resolveNotificationId(null, sourceNotification);
    }
    if (!idToMark && !sourceNotification) return;

    applyNotificationRead(idToMark, sourceNotification);

    if (!idToMark) return;

    try {
      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      const response = await fetch(
        getApiUrl(`/api/user/notifications/${idToMark}/read`),
        {
          method: "PUT",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Failed to mark notification as read:",
          response.status,
          errorText
        );
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
      fetchNotifications();
    }
  };

  // Clear all notifications
  const clearNotifications = async () => {
    try {
      const token =
        (typeof window !== "undefined" && localStorage.getItem("token")) ||
        null;
      await fetch(getApiUrl("/api/user/notifications/clear"), {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setNotifications([]);
      setNotificationUnreadCount(0);
      setNotificationsPagination({
        currentPage: 1,
        hasNextPage: false,
        totalItems: 0,
      });
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  // Function to get updated poem data
  const getUpdatedPoem = (poemId) => {
    return poemUpdates[poemId] || null;
  };

  // Function to clear poem update
  const clearPoemUpdate = (poemId) => {
    setPoemUpdates((prev) => {
      const newUpdates = { ...prev };
      delete newUpdates[poemId];
      return newUpdates;
    });
  };

  // Chat modal functions
  const openChatModal = (preSelectedUser = null) => {
    // Clear any existing state first
    setCurrentConversation(null);
    setMessages([]);
    setChatModalPreSelectedUser(preSelectedUser);
    setIsChatModalOpen(true);
  };

  const closeChatModal = () => {
    setIsChatModalOpen(false);
    setChatModalPreSelectedUser(null);
    // Clear current conversation and messages when closing modal
    setCurrentConversation(null);
    setMessages([]);
  };

  // Select conversation
  const selectConversation = async (conversation) => {
    // Leave current conversation room if any
    if (socket && currentConversation) {
      socket.emit("leaveConversation", currentConversation.conversationId);
    }

    const clearedUnread =
      typeof conversation.unreadCount === "number"
        ? conversation.unreadCount
        : 0;

    // Optimistically clear unread count locally immediately
    setConversations((prev) =>
      prev.map((conv) =>
        conv.conversationId === conversation.conversationId ||
        String(conv._id) === String(conversation._id)
          ? { ...conv, unreadCount: 0 }
          : conv
      )
    );

    if (clearedUnread > 0) {
      setUnreadCount((n) => Math.max(0, n - clearedUnread));
    }

    setCurrentConversation(conversation);
    // Join room immediately for real-time typing before messages load
    if (socket && conversation?.conversationId && !conversation.conversationId.startsWith('temp-')) {
      socket.emit("joinConversation", conversation.conversationId);
    }
    // Skip fetching messages for temporary conversations (they start with 'temp-')
    if (!conversation.conversationId || !conversation.conversationId.startsWith('temp-')) {
      setMessagesHasMore(false);
      setMessagesSkip(0);
      // Fetch messages (server also marks as read)
      await fetchMessages(conversation.conversationId);
      // Ensure server-side unread cleared as well
      await markAsRead(conversation.conversationId);
    } else {
      // For temp conversations, just clear messages
      setMessages([]);
      setMessagesHasMore(false);
      setMessagesSkip(0);
    }
  };

  // Leave current conversation
  const leaveConversation = () => {
    if (socket && currentConversation) {
      socket.emit("leaveConversation", currentConversation.conversationId);
    }
    setCurrentConversation(null);
    setMessages([]);
    setMessagesHasMore(false);
    setMessagesSkip(0);
  };

  const value = {
    socket,
    conversations,
    conversationsPagination,
    loadingMoreConversations,
    loadMoreConversations,
    currentConversation,
    messages,
    messagesHasMore,
    loadingOlderMessages,
    loadOlderMessages,
    unreadCount,
    unreadReady,
    isConnected,
    typingUsers,
    notifications,
    notificationUnreadCount,
    notificationsPagination,
    loadingMoreNotifications,
    loadMoreNotifications,
    poemUpdates,
    isChatModalOpen,
    chatModalPreSelectedUser,
    fetchConversations,
    fetchMessages,
    createConversation,
    sendMessage,
    setTyping,
    fetchUnreadCount,
    markAsRead,
    selectConversation,
    leaveConversation,
    fetchNotifications,
    patchNotification,
    markNotificationsAsRead,
    markNotificationAsRead,
    clearNotifications,
    getUpdatedPoem,
    clearPoemUpdate,
    openChatModal,
    closeChatModal,
    isUserTypingActive,
    conversationSeenAtMap,
    conversationSeenMessageIdMap,
    conversationDeliveredMessageIdMap,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
